import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BookOpen, Briefcase, Building2, Construction, FileText, Settings, Users } from 'lucide-react'
import { getIacrmConfig, hasIacrmConfig } from '../api'
import { useAuthSession } from '../../auth/session'
import { IacrmServicesPanel } from '../components/IacrmServicesPanel'
import { IacrmClientsPanel } from '../components/IacrmClientsPanel'
import { IacrmInvoicesPanel } from '../components/IacrmInvoicesPanel'
import { IacrmOperationsPanel } from '../components/IacrmOperationsPanel'
import { IacrmDocsTab } from '../components/IacrmDocsTab'
import { PlatformIacrmPage } from '../components/PlatformIacrmPage'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'

type IacrmTabId = 'services' | 'clients' | 'invoices' | 'operations' | 'docs'
type SuperAdminTabId = 'businesses' | 'docs'



export function IacrmDashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuthSession()
  const navigate = useNavigate()
  const isSuperAdmin = user?.roles.some((role) => role.slug === 'super-admin') ?? false
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')

  const [activeTab, setActiveTab] = useState<IacrmTabId>(() =>
    requestedTab === 'clients' ||
    requestedTab === 'services' ||
    requestedTab === 'invoices' ||
    requestedTab === 'operations' ||
    requestedTab === 'docs'
      ? requestedTab
      : 'clients',
  )

  const [superAdminTab, setSuperAdminTab] = useState<SuperAdminTabId>(() =>
    requestedTab === 'docs' ? requestedTab : 'businesses',
  )

  const config = getIacrmConfig()

  useEffect(() => {
    if (isSuperAdmin) {
      setSuperAdminTab(requestedTab === 'docs' ? requestedTab : 'businesses')
      return
    }

    setActiveTab(
      requestedTab === 'clients' ||
      requestedTab === 'services' ||
        requestedTab === 'invoices' ||
        requestedTab === 'operations' ||
        requestedTab === 'docs'
        ? requestedTab
        : 'clients',
    )
  }, [isSuperAdmin, requestedTab])

  function updateRequestedTab(nextTab: string | null) {
    const nextParams = new URLSearchParams(searchParams)
    if (nextTab) {
      nextParams.set('tab', nextTab)
    } else {
      nextParams.delete('tab')
    }
    setSearchParams(nextParams, { replace: true })
  }

  const NotConfiguredCallout = () => (
    <div className="flex flex-col items-center gap-3 rounded-xl bg-muted/40 px-6 py-8 text-center">
      <p className="text-sm text-muted-foreground">{t('iacrm.apiNotConfigured')}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => navigate('/settings?tab=api')}
      >
        <Settings className="size-4" />
        {t('iacrm.configureApi')}
      </Button>
    </div>
  )

  if (isSuperAdmin) {
    const superAdminNeedsConfig = superAdminTab === 'businesses' && !hasIacrmConfig(config)

    return (
      <section className="app-section">
        <Tabs
          value={superAdminTab}
          onValueChange={(value) => {
            const next = value as SuperAdminTabId
            setSuperAdminTab(next)
            updateRequestedTab(next === 'businesses' ? null : next)
          }}
        >
          <PageHeader
            title={t('iacrm.title')}
            right={
              <PageHeaderToolbar>
                <TabsList>
                  <TabsTrigger value="businesses">
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 className="size-3.5" />
                      <span>{t('iacrm.tabs.businesses')}</span>
                    </span>
                  </TabsTrigger>
                  <TabsTrigger value="docs">
                    <span className="inline-flex items-center gap-1.5">
                      <BookOpen className="size-3.5" />
                      <span>{t('iacrm.tabs.docs')}</span>
                    </span>
                  </TabsTrigger>
                </TabsList>
              </PageHeaderToolbar>
            }
          />

          {/* Beta alert */}
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
            <Construction className="mt-0.5 size-4 shrink-0" />
            <p>{t('iacrm.betaAlert')}</p>
          </div>

          <TabsContent value="businesses" className="mt-4 space-y-4">
            {superAdminNeedsConfig ? <NotConfiguredCallout /> : <PlatformIacrmPage />}
          </TabsContent>

          <TabsContent value="docs" className="mt-4">
            <IacrmDocsTab />
          </TabsContent>
        </Tabs>
      </section>
    )
  }

  const dataTabsNeedConfig: IacrmTabId[] = ['clients', 'services', 'operations']
  const needsConfig = dataTabsNeedConfig.includes(activeTab) && !hasIacrmConfig(config)

  return (
    <section className="app-section">
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const next = value as IacrmTabId
          setActiveTab(next)
          updateRequestedTab(next === 'clients' ? null : next)
        }}
      >
        <PageHeader
          title={t('iacrm.title')}
          right={
            <PageHeaderToolbar>
              <TabsList>
                <TabsTrigger value="clients">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="size-3.5" />
                    <span>{t('iacrm.tabs.clients')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="services">
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="size-3.5" />
                    <span>{t('iacrm.tabs.services')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="invoices">
                  <span className="inline-flex items-center gap-1.5">
                    <FileText className="size-3.5" />
                    <span>{t('iacrm.tabs.invoices')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="operations">
                  <span className="inline-flex items-center gap-1.5">
                    <Settings className="size-3.5" />
                    <span>{t('iacrm.tabs.operations')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="docs">
                  <span className="inline-flex items-center gap-1.5">
                    <BookOpen className="size-3.5" />
                    <span>{t('iacrm.tabs.docs')}</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </PageHeaderToolbar>
          }
        />

        {/* Beta alert */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
          <Construction className="mt-0.5 size-4 shrink-0" />
          <p>{t('iacrm.betaAlert')}</p>
        </div>

        <TabsContent value="clients" className="mt-4 space-y-4">
          {needsConfig ? <NotConfiguredCallout /> : <IacrmClientsPanel />}
        </TabsContent>

        <TabsContent value="services" className="mt-4 space-y-4">
          {needsConfig ? <NotConfiguredCallout /> : <IacrmServicesPanel />}
        </TabsContent>

        <TabsContent value="invoices" className="mt-4 space-y-4">
          <IacrmInvoicesPanel />
        </TabsContent>

        <TabsContent value="operations" className="mt-4 space-y-4">
          {needsConfig ? <NotConfiguredCallout /> : <IacrmOperationsPanel />}
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <IacrmDocsTab />
        </TabsContent>
      </Tabs>
    </section>
  )
}
